/**
 * Iter 166f · OportunidadesCpvWidget
 *
 * Resumen de oportunidades públicas con plazo abierto agrupadas por familia
 * CPV. Cada tarjeta es clickable y abre el Buscador PLCSP pre-filtrado por
 * ese prefijo (`/licitaciones?cpv=XX`).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, TrendingUp, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { CPV_GROUPS } from "@/lib/cpv_groups";

const GROUP_COLORS = {
  Obras: "#E08A1F",
  Suministros: "#1E5BCC",
  Servicios: "#0F8A5F",
};

function findGroup(prefix) {
  const c = String(prefix || "");
  let best = null;
  for (const g of CPV_GROUPS) {
    if (c.startsWith(g.code) && (!best || g.code.length > best.code.length)) {
      best = g;
    }
  }
  return best;
}

export default function OportunidadesCpvWidget() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/placsp/cpv-summary?digits=2&top=8");
        if (!alive) return;
        const enriched = (data.groups || []).map((g) => {
          const meta = findGroup(g.prefix);
          return {
            ...g,
            label: meta?.label || `CPV ${g.prefix}`,
            group: meta?.group || "Otros",
            color: GROUP_COLORS[meta?.group] || "#888",
          };
        });
        setRows(enriched);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message || "Error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-[var(--legatum-border)] bg-[var(--legatum-surface)] p-4 flex items-center gap-2 text-sm text-[var(--legatum-text-muted)]" data-testid="oportunidades-cpv-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Calculando oportunidades por familia CPV…
      </div>
    );
  }
  if (err || rows.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-[var(--legatum-border)] bg-[var(--legatum-surface)] p-4"
      data-testid="oportunidades-cpv-widget"
    >
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
               style={{ background: "linear-gradient(135deg, #C5A572, #E08A1F)" }}>
            <TrendingUp className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-[var(--legatum-text-muted)] flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> Oportunidades de obra hoy · por subgrupo CPV
            </div>
            <div className="text-[10px] text-[var(--legatum-text-muted)]">
              Foco obras (CPV 45) · plazo abierto · click para abrir el buscador filtrado
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {rows.map((r) => (
          <button
            key={r.prefix}
            onClick={() => navigate(`/licitaciones?cpv=${r.prefix}`)}
            className="text-left rounded-xl border border-[var(--legatum-border)] p-3 hover:border-[var(--legatum-accent)] hover:bg-[var(--legatum-elevated)]/60 transition-all group"
            style={{ borderLeft: `4px solid ${r.color}` }}
            data-testid={`oportunidad-cpv-${r.prefix}`}
            title={`${r.label} · ${r.count.toLocaleString("es-ES")} licitaciones con plazo abierto`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span
                className="text-[10px] font-mono font-bold tabular-nums px-1.5 py-0.5 rounded shrink-0"
                style={{ background: `${r.color}22`, color: r.color }}
              >
                {r.prefix}
              </span>
              <span className="text-[9px] uppercase tracking-wider text-[var(--legatum-text-muted)] truncate">
                {r.group}
              </span>
            </div>
            <div className="text-xl font-extrabold tabular-nums leading-none mb-1" style={{ color: r.color }}>
              {r.count.toLocaleString("es-ES")}
            </div>
            <div className="text-[11px] text-[var(--legatum-text-secondary)] line-clamp-2 leading-tight">
              {r.label}
            </div>
          </button>
        ))}
      </div>
    </motion.div>
  );
}
